import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Users, Building2, DollarSign } from 'lucide-react';
import { resourcesAPI, resourceTypesAPI, clientsAPI } from '../services/api';
import { nikaoColors } from '../theme/nikaoTheme';
import { formatCurrency } from '../utils/chartUtils';
import toast from 'react-hot-toast';

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    resource_type_id: '',
    day_rate: 0,
    utilization: 100,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resourcesRes, resourceTypesRes, clientsRes] = await Promise.all([
        resourcesAPI.getAll(),
        resourceTypesAPI.getAll(),
        clientsAPI.getAll()
      ]);
      
      console.log('Resources response:', resourcesRes.data);
      console.log('Resource types response:', resourceTypesRes.data);
      console.log('Clients response:', clientsRes.data);
      
      // Ensure unique resources by ID
      const uniqueResources = Array.isArray(resourcesRes.data) 
        ? resourcesRes.data.filter((resource, index, self) => 
            index === self.findIndex(r => r.id === resource.id)
          )
        : [];
      
      const uniqueResourceTypes = Array.isArray(resourceTypesRes.data) 
        ? resourceTypesRes.data.filter((resourceType, index, self) => 
            index === self.findIndex(rt => rt.id === resourceType.id)
          )
        : [];
      
      const uniqueClients = Array.isArray(clientsRes.data) 
        ? clientsRes.data.filter((client, index, self) => 
            index === self.findIndex(c => c.id === client.id)
          )
        : [];
      
      setResources(uniqueResources);
      setResourceTypes(uniqueResourceTypes);
      setClients(uniqueClients);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Resource name is required');
      return;
    }

    if (!formData.resource_type_id) {
      toast.error('Resource type is required');
      return;
    }

    try {
      // If resource_type_id is a string (new resource type), ensure it exists first
      let finalResourceTypeId = formData.resource_type_id;
      
      // Check if it's a new resource type name (not a number and not empty)
      if (formData.resource_type_id && isNaN(formData.resource_type_id) && formData.resource_type_id.trim() !== '') {
        // This is a new resource type name, create it first
        const ensureResponse = await resourceTypesAPI.ensureExists({
          name: formData.resource_type_id,
          description: `Auto-created for resource: ${formData.name}`
        });
        
        finalResourceTypeId = ensureResponse.data.resourceType.id;
        
        if (ensureResponse.data.created) {
          toast.success(`Created new resource type: ${formData.resource_type_id}`);
        }
      }

      const resourceData = {
        ...formData,
        resource_type_id: finalResourceTypeId,
        utilization: formData.utilization / 100 // Convert percentage to decimal
      };

      console.log('Form data:', formData);
      console.log('Final resource type ID:', finalResourceTypeId);
      console.log('Resource data being sent:', resourceData);

      if (editingResource) {
        await resourcesAPI.update(editingResource.id, resourceData);
        toast.success('Resource updated successfully');
      } else {
        await resourcesAPI.create(resourceData);
        toast.success('Resource created successfully');
      }
      
      setShowModal(false);
      setEditingResource(null);
      setFormData({
        name: '',
        email: '',
        resource_type_id: '',
        day_rate: 0,
        utilization: 100,
        notes: ''
      });
      loadData();
    } catch (error) {
      console.error('Error saving resource:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save resource';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (resource) => {
    setEditingResource(resource);
      setFormData({
        name: resource.name || '',
        email: resource.email || '',
        resource_type_id: resource.resource_type_id || '',
        day_rate: resource.day_rate || 0,
        utilization: (resource.utilization || 1.0) * 100, // Convert decimal to percentage
      notes: resource.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resource? This will also remove all associated SOW entries.')) {
      return;
    }

    try {
      await resourcesAPI.delete(id);
      toast.success('Resource deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting resource:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete resource';
      toast.error(errorMessage);
    }
  };

  const openCreateModal = () => {
    setEditingResource(null);
    setFormData({
      name: '',
      email: '',
      resource_type_id: '',
      day_rate: 0,
      utilization: 1.0,
      notes: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingResource(null);
    setFormData({
      name: '',
      email: '',
      resource_type_id: '',
      day_rate: 0,
      utilization: 1.0,
      notes: ''
    });
  };

  const getResourceTypeName = (resourceTypeId) => {
    const resourceType = resourceTypes.find(rt => rt.id === parseInt(resourceTypeId));
    return resourceType ? resourceType.name : '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading resources...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold" style={{ color: nikaoColors.text }}>
          Resources Management
        </h1>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
          style={{ backgroundColor: nikaoColors.primary }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Resource
        </button>
      </div>

      {/* Resources Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Resource
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Day Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {resources.map((resource) => (
                <tr key={resource.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium" style={{ color: nikaoColors.text }}>
                        {resource.name}
                      </div>
                      {resource.email && (
                        <div className="text-sm" style={{ color: nikaoColors.textSecondary }}>
                          {resource.email}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getResourceTypeName(resource.resource_type_id)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: nikaoColors.text }}>
                    {formatCurrency(resource.day_rate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: nikaoColors.text }}>
                    {((Number(resource.utilization) || 0) * 100).toFixed(0)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(resource)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(resource.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {resources.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 mx-auto mb-4" style={{ color: nikaoColors.textSecondary }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: nikaoColors.text }}>
            No resources found
          </h3>
          <p className="text-sm" style={{ color: nikaoColors.textSecondary }}>
            Get started by creating your first resource.
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold" style={{ color: nikaoColors.text }}>
                {editingResource ? 'Edit Resource' : 'Add Resource'}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: nikaoColors.text }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter resource name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: nikaoColors.text }}>
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: nikaoColors.text }}>
                  Resource Type *
                </label>
                <select
                  value={formData.resource_type_id}
                  onChange={(e) => setFormData({ ...formData, resource_type_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select resource type</option>
                  {resourceTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: nikaoColors.text }}>
                  Day Rate (£)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.day_rate}
                  onChange={(e) => setFormData({ ...formData, day_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: nikaoColors.text }}>
                  Utilization (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.utilization}
                  onChange={(e) => setFormData({ ...formData, utilization: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="100"
                />
                <p className="text-xs text-gray-500 mt-1">Enter as percentage (e.g., 80 for 80%)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: nikaoColors.text }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter additional notes"
                  rows="3"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-white rounded-md hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: nikaoColors.primary }}
                >
                  {editingResource ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resources; 